import LearningManagerStore from 'Stores/LearningManagerStore';
import AssignUsersStore from 'Stores/AssignUsersStore';
import { isRetryableError } from 'LearningManager/helpers/concurrency';

export type UserIdsCache = Map<string, Promise<string[]>>;

const emptyUnlessRetryable = <T>(error: unknown): T[] => {
	if (isRetryableError(error)) throw error;
	return [];
};

const fromScope = <T>(
	scopes: string[],
	scope: string,
	fetch: () => Promise<T[]>
): Promise<T[]> =>
	scopes.includes(scope)
		? fetch().catch((error) => emptyUnlessRetryable<T>(error))
		: Promise.resolve([]);

const cacheKey = (kind: string, tenantId: number | undefined, ids: (string | number)[]) =>
	`${kind}:${tenantId ?? ''}:${ids.map(String).sort().join(',')}`;

const cached = (
	cache: UserIdsCache | undefined,
	key: string,
	fetch: () => Promise<string[]>
): Promise<string[]> => {
	if (!cache) return fetch();
	const hit = cache.get(key);
	if (hit) return hit;
	const request = fetch();
	cache.set(key, request);
	request.catch(() => cache.delete(key));
	return request;
};

export const resolveAssignmentUserIds = async (
	learningManagerStore: LearningManagerStore,
	assignUsersStore: AssignUsersStore,
	assignmentId: number,
	givenScopes: string[],
	givenTenantId?: number,
	cache?: UserIdsCache
): Promise<string[]> => {
	const needsTenant =
		givenScopes.includes('roles') || givenScopes.includes('group_labels');
	const needsDetail =
		!givenScopes.filter(Boolean).length ||
		(needsTenant && givenTenantId === undefined);
	const detail = needsDetail
		? await learningManagerStore
				.getAssignmentSource(assignmentId)
				.catch((error) => {
					if (isRetryableError(error)) throw error;
					return undefined;
				})
		: undefined;
	const scopes: string[] = givenScopes.filter(Boolean).length
		? givenScopes
		: detail?.scopes ?? [];
	const detailTenantId = Number(detail?.tenant_id);
	const tenantId =
		givenTenantId ??
		(Number.isFinite(detailTenantId) && detailTenantId > 0
			? detailTenantId
			: undefined);
	const tenantScopes = tenantId === undefined ? [] : scopes;
	const [
		scopeUserIds,
		groupUserIds,
		labelUserIds,
		roleUserIds
	] = await Promise.all([
		fromScope(scopes, 'users', () =>
			learningManagerStore
				.getAssignmentSourceScopeUsers(assignmentId)
				.then((users) => users.map((u) => u.id))
		),
		fromScope(scopes, 'groups', () =>
			learningManagerStore
				.getAssignmentSourceScopeGroups(assignmentId)
				.then((groups) =>
					Promise.all(
						[...new Set(groups.map((g) => g.id))].map((groupId) =>
							cached(cache, cacheKey('group', undefined, [groupId]), () =>
								assignUsersStore.fetchUserIdsByGroups([groupId])
							)
						)
					)
				)
				.then((lists) => ([] as string[]).concat(...lists))
		),
		fromScope(tenantScopes, 'group_labels', () =>
			learningManagerStore
				.getAssignmentSourceScopeGroupLabels(assignmentId)
				.then((labels) => {
					const labelIds = labels.map((l) => l.group_id).filter(Boolean);
					return cached(cache, cacheKey('labels', tenantId, labelIds), () =>
						assignUsersStore.fetchUserIdsByGroupLabels(
							tenantId as number,
							labelIds
						)
					);
				})
		),
		fromScope(tenantScopes, 'roles', () =>
			learningManagerStore
				.getAssignmentSourceScopeRoles(assignmentId)
				.then((roles) => {
					const roleIds = roles.map((r) => Number(r.id));
					return cached(cache, cacheKey('roles', tenantId, roleIds), () =>
						assignUsersStore.fetchUserIdsByRoles(
							tenantId as number,
							roleIds
						)
					);
				})
		)
	]);
	return [
		...new Set([
			...scopeUserIds,
			...groupUserIds,
			...labelUserIds,
			...roleUserIds
		])
	];
};
